import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";
import { canSoftDeleteFlowRecord, resolveAuthUserId } from "../../app/helpers/flowEditPageHelper";
import { canOperateCmiRecord } from "../../app/helpers/flowTraySurfaceHelper";
import { normalizeSpecialistRoleKey } from "../../app/helpers/activeSpecialistRoleHelper";
import { resolveRecordExcelRoleKey } from "../../app/helpers/flowEngineConfigHelper";
import RecordExcelUpdateRowButton from "./components/RecordExcelUpdateRowButton";

const SpecialistTableRowActions = ({
  record,
  onEdit,
  onDelete,
  editTipKey,
  editTipFallback,
  deleteTipKey,
  deleteTipFallback,
  tooltipScope = "row",
  moduleProcess = "",
  onExcelApplied
}) => {
  const { t } = useTranslation();
  const configuration = useSelector((state) => state.configuration);
  const flowEngine = useSelector((state) => state.flowEngine);
  const authUser = useSelector((state) => state.auth?.user);
  const { flowEngineEnabled, canEdit, canDelete, selectedRole, roleKey, availableRoles } = useActiveSpecialistRole();
  const canOperateRecord = !flowEngineEnabled || canOperateCmiRecord({
    record,
    roleKey,
    roleId: selectedRole?.id,
    userId: resolveAuthUserId(authUser)
  });
  const extraRoleKeys = useMemo(
    () => availableRoles.map((role) => role.roleKey || normalizeSpecialistRoleKey(role)).filter(Boolean),
    [availableRoles]
  );
  const showEditButton = !flowEngineEnabled || canEdit;
  const showDeleteButton = canOperateRecord && canSoftDeleteFlowRecord({
    record,
    flowEngineEnabled,
    canDelete,
    selectedRole,
    flowEngine
  });
  const excelButton = (
    <RecordExcelUpdateRowButton
      record={record}
      moduleProcess={moduleProcess}
      onApplied={onExcelApplied}
      tooltipScope={tooltipScope}
    />
  );
  const showExcelButton = Boolean(
    moduleProcess
    && (record?.id || record?.name)
    && resolveRecordExcelRoleKey(configuration, moduleProcess, roleKey, extraRoleKeys)
  );
  const editTipId = `editTip-${tooltipScope}`;
  const deleteTipId = `deleteTip-${tooltipScope}`;

  return (
    <div>
      {showEditButton && (
        <>
          <button
            type="button"
            data-tip
            data-for={editTipId}
            className="table-button-edit"
            onClick={onEdit}
          >
            <i className="fa fa-edit" />
          </button>
          <ReactTooltip id={editTipId} place="left" effect="solid">
            {t(editTipKey, editTipFallback)}
          </ReactTooltip>
        </>
      )}
      {showExcelButton && excelButton}
      {showDeleteButton && (
        <>
          <button
            type="button"
            data-tip
            data-for={deleteTipId}
            className="table-button-delete"
            onClick={onDelete}
          >
            <i className="fa fa-trash" />
          </button>
          <ReactTooltip id={deleteTipId} place="left" effect="solid">
            {t(deleteTipKey, deleteTipFallback)}
          </ReactTooltip>
        </>
      )}
      {flowEngineEnabled && !showEditButton && !showDeleteButton && !showExcelButton && (
        <span className="text-muted small">{t("general.flow-engine.view-only", "Solo lectura")}</span>
      )}
    </div>
  );
};

SpecialistTableRowActions.propTypes = {
  record: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  editTipKey: PropTypes.string.isRequired,
  editTipFallback: PropTypes.string,
  deleteTipKey: PropTypes.string.isRequired,
  deleteTipFallback: PropTypes.string,
  tooltipScope: PropTypes.string,
  moduleProcess: PropTypes.string,
  onExcelApplied: PropTypes.func
};

export default SpecialistTableRowActions;
